import {
  Component,
  computed,
  inject, input,
  OnInit,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';

import { FormContentComponent } from '../form-content/form-content.component';
import {
    OptionItem,
    StepContent,
    BudgetRange,
    Timeline
} from 'matchmaking/models';
import {
    INVESTMENT_GOALS,
    INVESTMENT_LEVELS,
    INVESTMENT_TIMELINE,
} from 'matchmaking/constants';
import { FormOptionComponent } from '../form-option/form-option.component';
import {
    MatchmakingApiService,
    MatchmakingDataService,
    MatchmakingTranslateService,
    TrackingMatchmakingSnowplowService
} from 'matchmaking/services';
import { FormSubmitButtonComponent } from '../form-submit-button/form-submit-button.component';
import { FormOptionListComponent } from '../form-option-list/form-option-list.component';
import { CheckboxComponent } from '@sirius/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { FormBackButtonComponent } from '../form-back-button/form-back-button.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'matchmaking-step-project',
    imports: [FormContentComponent, FormOptionComponent, FormSubmitButtonComponent, FormOptionListComponent, ReactiveFormsModule, FormBackButtonComponent, TranslateModule, CheckboxComponent],
    templateUrl: './step-project.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './step-project.component.scss'
})
export class StepProjectComponent implements OnInit {
    translateService = inject(MatchmakingTranslateService);
    dataService = inject(MatchmakingDataService);
    apiService = inject(MatchmakingApiService);
    trackingService = inject(TrackingMatchmakingSnowplowService);

    content = input.required<StepContent>();

    translations = computed(() => this.translateService.translations());

    //Timer created because of a raise condition with Sirius component that hinders update of translation
    initialLoadHadFinished = signal(false);

    readonly investmentCheckboxControl = new FormControl(false);
    readonly investmentGoals = INVESTMENT_GOALS;
    readonly investmentLevels = INVESTMENT_LEVELS;
    readonly investmentTimeline = INVESTMENT_TIMELINE;

    readonly isSubmitDisabled = computed(() => {
        const currentConfig = this.dataService.config();
        return (
            !currentConfig?.projectGoal || !currentConfig?.budgetRange || !currentConfig?.timeline
        );
    });

    constructor() {
        const currentDecisionMaker = this.dataService.config()?.decisionMaker ?? false;
        this.investmentCheckboxControl.setValue(currentDecisionMaker);
        this.investmentCheckboxControl.valueChanges.subscribe((checked) => {
            this.setInvestmentCheckbox(Boolean(checked));
        });
    }

    ngOnInit() {
        //TODO fix this - see above comment where the timer variable is declared
        setTimeout(() => {
            this.initialLoadHadFinished.update(() => true);
        }, 0);

        const config = this.dataService.config();
        if (config) {
            this.trackingService.trackStepStart({ step_number: 4, step_name: config.currentStep });
        }
    }

    setSelected(option: OptionItem) {
        this.dataService.updateConfig({ projectGoal: option.title });
    }

    setSelectedInvestment(option: OptionItem) {
        this.dataService.updateConfig({ budgetRange: option.title as BudgetRange });
    }

    setSelectedTimeline(option: OptionItem) {
        this.dataService.updateConfig({ timeline: option.title as Timeline });
    }

    setInvestmentCheckbox(checked: boolean) {
        this.dataService.updateConfig({ decisionMaker: checked });
    }

    handleCheckboxChange(event: Event) {
        const checkbox = event.target as HTMLInputElement;
        this.setInvestmentCheckbox(checkbox.checked);
    }

    handleBack() {
        this.apiService.goToPreviousStep();
    }

    submit() {
        this.apiService.submit();
    }

    optionIsSelected(option: OptionItem, selectedOption?: string) {
        if (option.title === selectedOption) {
            return true;
        }

        if (this.translateService.getTranslation(option.title) === selectedOption) {
            return true;
        }

        return false;
    }
}
